































































































































































































































































































import {
  SfSelect, SfButton, SfInput, SfCheckbox, SfSelectOption,
} from '~/components/StorefrontUi';
import { required, min, oneOf } from 'vee-validate/dist/rules';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import {
  ref,
  watch,
  computed,
  defineComponent,
  onBeforeMount,
  useRouter,
  useContext,
} from '@nuxtjs/composition-api';
import omitDeep from 'omit-deep';
import { PropType } from 'vue';
import { CountryCodeEnum, useCountrySearch } from '~/composables';
import type { Countries, Country, UseCountrySearchParams } from '~/composables';
import addressGetter from '~/modules/customer/getters/addressGetter';
import type { TransformedCustomerAddress } from '~/modules/customer/composables/types';

extend('required', {
  ...required,
  message: 'This field is required',
});

extend('min', {
  ...min,
  message: 'The field should have at least {length} characters',
});

extend('oneOf', {
  ...oneOf,
  message: 'Invalid country',
});

export default defineComponent({
  name: 'AddressForm',

  components: {
    SfInput,
    SfButton,
    SfSelect,
    SfSelectOption,
    SfCheckbox,
    ValidationProvider,
    ValidationObserver,
  },

  props: {
    address: {
      type: Object as PropType<TransformedCustomerAddress>,
      default: () => ({
        apartment: '',
        city: '',
        vat_id: '',
        // email: '',
        company: '',
        country_code: '' as CountryCodeEnum,
        firstname: '',
        lastname: '',
        postcode: '',
        region: {
          region_code: '',
          region_id: 0,
        },
        street: '',
        telephone: '',
        default_shipping: false,
        default_billing: false,
        customAttributes: [],
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { load: loadCountries, search: searchCountry } = useCountrySearch();
    const router = useRouter();
    const context = useContext();

    const form = ref<TransformedCustomerAddress | null>(null);

    const companyEmail = ref('');
    const invoiceEmail = ref('');
    const getAttributeValue = (customAttributes, code: string) => customAttributes?.find((attr) => attr.code === code)?.value || '';
    const getCustomAttributes = () => {
      const customAttributes = [
        { code: 'company_email', value: companyEmail.value },
        { code: 'invoice_email', value: invoiceEmail.value },
      ];

      return customAttributes;
    };

    const countries = ref<Countries[]>([]);
    const countriesList = computed(() => addressGetter.countriesList(countries.value));
    const country = ref<Country | null>(null);
    const regionInformation = computed(() => addressGetter.regionList(country.value));

    const updateCountry = async (params: UseCountrySearchParams) => {
      country.value = await searchCountry(params);
      form.value.region = { region_id: null, region_code: '' };
    };

    watch(
      () => props.address,
      () => {
        companyEmail.value = getAttributeValue(props.address.customAttributes, 'company_email');
        invoiceEmail.value = getAttributeValue(props.address.customAttributes, 'invoice_email');
        form.value = {
          apartment: props.address.apartment ?? '',
          city: props.address.city,
          vat_id: props.address.vat_id,
          company: props.address.company,
          // email: props.address.email,
          country_code: props.address.country_code,
          firstname: props.address.firstname,
          lastname: props.address.lastname,
          postcode: props.address.postcode,
          region: {
            region_code: '',
            region_id: null,
            ...props.address.region,
          },
          street: props.address.street,
          telephone: props.address.telephone,
          default_shipping: props.address.default_shipping || false,
          default_billing: props.address.default_billing || false,
        } as TransformedCustomerAddress;
      },
      { immediate: true },
    );

    const clear = async () => {
      await router.push(
        context.localeRoute({ name: 'customer-addresses-details' }),
      );
    };

    const submitForm = () => {
      const regionId = regionInformation.value.find(
        (r) => r.abbreviation === form.value.region.region_code,
      )?.id;
      form.value.region = regionId
        ? { region_id: regionId }
        : {
          ...form.value.region,
          region_code: '',
          region_id: null,
        };

      emit('submit', {
        form: {
          ...omitDeep(form.value, ['__typename']),
          customAttributes: getCustomAttributes(),
        },
        // TODO: Handle Error
        onError: () => {},
      });
    };

    onBeforeMount(async () => {
      countries.value = await loadCountries();
      if (props.address.country_code) {
        country.value = await searchCountry({ id: props.address.country_code });
      }
    });

    return {
      form,
      clear,
      submitForm,
      countriesList,
      regionInformation,
      updateCountry,
      companyEmail,
      invoiceEmail,
    };
  },
});
